import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI, providersAPI } from '../services/api';
import { Car, MapPin, AlertCircle, CheckCircle, Loader, X, XCircle } from 'lucide-react';
import { Provider } from '../types';

interface DriverRequest {
  _id: string;
  customerId: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  bookingDate: string;
  timeSlot?: string; // Optional for backward compatibility
  tripStartDateTime?: string; // Scheduled trip start datetime (ISO string)
  tripEndDateTime?: string; // Scheduled trip end datetime (ISO string)
  pickupLocation: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  dropoffLocation: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  distance: number;
  totalDistance?: number; // For multi-destination trips
  intermediateDestinations?: Array<{
    latitude: number;
    longitude: number;
    address?: string;
    order: number;
  }>;
  numberOfNights: number;
  notes?: string;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string;
  createdAt: string;
  bookingId?: string | { toString(): string };
}

interface DriverRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DriverRequestsModal: React.FC<DriverRequestsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<DriverRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acceptingRequest, setAcceptingRequest] = useState<string | null>(null);
  const [assigningRequest, setAssigningRequest] = useState<string | null>(null);
  const [ignoringRequest, setIgnoringRequest] = useState<string | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [drivers, setDrivers] = useState<Provider[]>([]);
  const [conflictData, setConflictData] = useState<any>(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [overrideAction, setOverrideAction] = useState<'cancel' | 'reassign'>('cancel');
  const [newDriverForReassign, setNewDriverForReassign] = useState<string>('');
  const [currentRequestId, setCurrentRequestId] = useState<string>('');
  const [currentDriverId, setCurrentDriverId] = useState<string>('');

  const isAdmin = user?.role === 'admin';
  const isDriver = user?.skills?.includes('driver');

  useEffect(() => {
    if (isOpen) {
      fetchDriverRequests();
      if (isAdmin) {
        fetchDrivers();
      }
      // Android WebView fix: Force refresh after modal opens to ensure data loads
      const timer = setTimeout(() => {
        fetchDriverRequests();
        if (isAdmin) {
          fetchDrivers();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAdmin]);

  const fetchDrivers = async () => {
    try {
      const response = await providersAPI.getProviders();
      const allProviders = response.data?.providers || [];
      const driverList = allProviders.filter((p: Provider) => p.skills?.includes('driver'));
      setDrivers(driverList);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchDriverRequests = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getDriverRequests();
      setRequests(response.data || []);
      setError('');
    } catch (error: any) {
      console.error('Error fetching driver requests:', error);
      if (error.response?.status !== 403) {
        setError(error.response?.data?.message || 'Failed to load driver requests');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIgnoreRequest = async (requestId: string) => {
    if (window.confirm('Are you sure you want to ignore this request? It will be hidden from your view.')) {
      try {
        setIgnoringRequest(requestId);
        setError('');
        
        await bookingsAPI.ignoreDriverRequest(requestId);
        
        // Refresh requests list to remove the ignored request
        await fetchDriverRequests();
        
        alert('Request ignored successfully');
      } catch (error: any) {
        console.error('Error ignoring driver request:', error);
        const errorMessage = error.response?.data?.message || 'Failed to ignore request';
        setError(errorMessage);
        alert(errorMessage);
      } finally {
        setIgnoringRequest(null);
      }
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setAcceptingRequest(requestId);
      setError('');
      
      await bookingsAPI.acceptDriverRequest(requestId);
      
      // Refresh requests list
      await fetchDriverRequests();
      
      // Close modal and navigate
      onClose();
      navigate('/provider-bookings');
    } catch (error: any) {
      console.error('Error accepting driver request:', error);
      setError(error.response?.data?.message || 'Failed to accept request');
      alert(error.response?.data?.message || 'Failed to accept request');
    } finally {
      setAcceptingRequest(null);
    }
  };

  const handleAdminAssign = async (requestId: string) => {
    if (!selectedDriver) {
      alert('Please select a driver to assign');
      return;
    }

    try {
      setAssigningRequest(requestId);
      setError('');
      
      // Admin assigns the request to a specific driver
      await bookingsAPI.adminAssignDriverRequest(requestId, selectedDriver);
      
      // Refresh requests list
      await fetchDriverRequests();
      setSelectedDriver('');
    } catch (error: any) {
      console.error('Error assigning driver request:', error);
      
      // Check if it's a conflict (409 status)
      if (error.response?.status === 409 && error.response?.data?.requiresOverride) {
        // Show conflict modal
        setConflictData(error.response.data.conflict);
        setCurrentRequestId(requestId);
        setCurrentDriverId(selectedDriver);
        setShowConflictModal(true);
      } else {
        setError(error.response?.data?.message || 'Failed to assign request');
        alert(error.response?.data?.message || 'Failed to assign request');
      }
    } finally {
      setAssigningRequest(null);
    }
  };

  const handleConflictOverride = async () => {
    try {
      setAssigningRequest(currentRequestId);
      
      const payload: any = {
        driverId: currentDriverId,
        force: true,
        overrideAction
      };
      
      if (overrideAction === 'reassign' && newDriverForReassign) {
        payload.newDriverId = newDriverForReassign;
      }
      
      // Call the API with force flag
      await bookingsAPI.adminAssignDriverRequest(currentRequestId, currentDriverId, payload);
      
      // Close conflict modal and refresh
      setShowConflictModal(false);
      setConflictData(null);
      await fetchDriverRequests();
      setSelectedDriver('');
    } catch (error: any) {
      console.error('Error overriding conflict:', error);
      alert(error.response?.data?.message || 'Failed to override conflict');
    } finally {
      setAssigningRequest(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const diff = expiry - now;
    
    if (diff <= 0) return 'Expired';
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    return `${minutes}m ${seconds}s`;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-[9998]" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="relative flex min-h-screen items-center justify-center p-4 z-[9999]">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <Car className="h-6 w-6 text-black600 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isAdmin ? 'Driver Requests (Admin)' : 'Driver Requests'}
                </h2>
                <p className="text-sm text-gray-600">
                  {isAdmin ? 'Assign requests to drivers' : 'Accept ride requests from customers'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-scroll h-full p-6 max-h-[calc(90vh-120px)]">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <Loader className="h-8 w-8 animate-spin text-black600 mx-auto mb-4" />
                <p className="text-gray-600">Loading requests...</p>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Requests</h3>
                <p className="text-gray-600">
                  There are no pending driver requests at the moment.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => {
                  const timeRemaining = getTimeRemaining(request.expiresAt);
                  const isExpiring = new Date(request.expiresAt).getTime() - new Date().getTime() < 120000;

                  return (
                    <div
                      key={request._id}
                      className={`border rounded-lg p-4 ${
                        isExpiring ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                      }`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">
                              {request.customerId.name}
                            </h3>
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-xs font-medium text-black600 bg-white px-2 py-0.5 rounded">
                              Request ID: {request._id.toString().slice(-8).toUpperCase()}
                            </p>
                            {request.bookingId && (
                              <p className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                Booking ID: {typeof request.bookingId === 'string' 
                                  ? request.bookingId.slice(-8).toUpperCase() 
                                  : request.bookingId.toString().slice(-8).toUpperCase()}
                              </p>
                            )}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isExpiring ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                          }`}
                        >
                          ⏱️ {timeRemaining}
                        </span>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-gray-500">📅 </span>
                          <span className="text-gray-700">{formatDate(request.bookingDate)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">🕐 </span>
                          <span className="text-gray-700">
                            {request.tripStartDateTime && request.tripEndDateTime ? (
                              <>
                                {new Date(request.tripStartDateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - {new Date(request.tripEndDateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              </>
                            ) : request.timeSlot ? (
                              request.timeSlot.split('-').map(formatTime).join(' - ')
                            ) : (
                              'N/A'
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">📍 </span>
                          <span className="text-gray-700">
                            {typeof (request.totalDistance || request.distance) === 'number' ? (request.totalDistance || request.distance).toFixed(2) : (request.totalDistance || request.distance) || '0.00'} km
                            {request.intermediateDestinations && request.intermediateDestinations.length > 0 && (
                              <span className="text-xs text-gray-500 ml-1">(round trip)</span>
                            )}
                          </span>
                        </div>
                        {request.numberOfNights > 0 && (
                          <div>
                            <span className="text-gray-500">🌙 </span>
                            <span className="text-gray-700">{request.numberOfNights} night(s)</span>
                          </div>
                        )}
                      </div>

                      {/* Locations */}
                      <div className="text-xs space-y-1 mb-3">
                        <div className="flex items-start">
                          <MapPin className="h-3 w-3 text-green-600 mr-1 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{request.pickupLocation.address || 'Pickup location'}</span>
                        </div>
                        
                        {/* Intermediate Destinations */}
                        {request.intermediateDestinations && request.intermediateDestinations.length > 0 && (
                          <>
                            {request.intermediateDestinations
                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                              .map((stop, index) => (
                                <div key={index} className="flex items-start">
                                  <MapPin className="h-3 w-3 text-blue-600 mr-1 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-600">
                                    Stop {stop.order || index + 1}: {stop.address || 'Intermediate location'}
                                  </span>
                                </div>
                              ))}
                          </>
                        )}
                        
                        <div className="flex items-start">
                          <MapPin className="h-3 w-3 text-red-600 mr-1 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">
                            {(() => {
                              // Check if it's a round trip (multi-destination trips are always round trips)
                              const isRoundTrip = request.intermediateDestinations && request.intermediateDestinations.length > 0;
                              
                              if (isRoundTrip) {
                                // For multi-destination trips, show final dropoff and indicate return to pickup
                                const finalDropoff = request.dropoffLocation.address || 'Dropoff location';
                                const pickupAddress = request.pickupLocation.address || 'pickup location';
                                return `Final Dropoff: ${finalDropoff} → Back to ${pickupAddress}`;
                              }
                              // For single destination, check if dropoff is same as pickup (round trip)
                              const isSameLocation = 
                                Math.abs(request.pickupLocation.latitude - request.dropoffLocation.latitude) < 0.0001 &&
                                Math.abs(request.pickupLocation.longitude - request.dropoffLocation.longitude) < 0.0001;
                              
                              if (isSameLocation) {
                                return `${request.dropoffLocation.address || 'Dropoff location'} (round trip)`;
                              }
                              return request.dropoffLocation.address || 'Dropoff location';
                            })()}
                          </span>
                        </div>
                      </div>

                      {request.notes && (
                        <div className="text-xs text-gray-600 mb-3 italic">
                          "{request.notes}"
                        </div>
                      )}

                      {/* Actions */}
                      {isAdmin ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedDriver}
                            onChange={(e) => setSelectedDriver(e.target.value)}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md"
                          >
                            <option value="">Select driver...</option>
                            {drivers.map((driver) => (
                              <option key={driver._id || driver.id} value={driver._id || driver.id}>
                                {driver.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAdminAssign(request._id)}
                            disabled={assigningRequest === request._id || !selectedDriver}
                            className="px-3 py-1 text-sm bg-black text-white rounded-md hover:bg-black700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {assigningRequest === request._id ? 'Assigning...' : 'Assign'}
                          </button>
                        </div>
                      ) : isDriver ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleIgnoreRequest(request._id)}
                            disabled={acceptingRequest === request._id || ignoringRequest === request._id}
                            className="flex-1 flex items-center justify-center px-3 py-3 text-base bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                          >
                            {ignoringRequest === request._id ? (
                              <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                Ignoring...
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-2" />
                                Ignore
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleAcceptRequest(request._id)}
                            disabled={acceptingRequest === request._id}
                            className="flex-1 flex items-center justify-center px-3 py-3 text-base bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {acceptingRequest === request._id ? (
                              <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                Accepting...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Accept Request
                              </>
                            )}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <button
              onClick={fetchDriverRequests}
              className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-base"
            >
              <Loader className="inline h-4 w-4 mr-2" />
              Refresh Requests
            </button>
          </div>
        </div>
      </div>

      {/* Conflict Resolution Modal */}
      {showConflictModal && conflictData && (
        <div className="fixed inset-0 z-[10000] overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000]" onClick={() => setShowConflictModal(false)}></div>
          <div className="relative flex min-h-screen items-center justify-center p-4 z-[10001]">
            <div className="relative bg-white rounded-lg shadow-2xl max-w-lg w-full p-6">
              <button
                onClick={() => setShowConflictModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>

              <div className="text-center mb-6">
                <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Driver Scheduling Conflict</h2>
                <p className="text-gray-600">
                  The selected driver already has a booking at this time
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-red-900 mb-2">Conflicting Booking:</h3>
                <div className="text-sm text-red-800 space-y-1">
                  <p><strong>Customer:</strong> {conflictData.customer?.name}</p>
                  <p><strong>Time:</strong> {
                    conflictData.tripStartDateTime && conflictData.tripEndDateTime ? (
                      `${new Date(conflictData.tripStartDateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - ${new Date(conflictData.tripEndDateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
                    ) : conflictData.timeSlot || 'N/A'
                  }</p>
                  <p><strong>Route:</strong> {conflictData.pickupLocation} → {conflictData.dropoffLocation}</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <p className="text-sm font-semibold text-gray-700">Choose how to proceed:</p>
                
                <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="override"
                    value="cancel"
                    checked={overrideAction === 'cancel'}
                    onChange={() => setOverrideAction('cancel')}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Cancel Previous Booking</div>
                    <div className="text-sm text-gray-600">
                      Cancel the existing booking and assign this new request to the driver
                    </div>
                  </div>
                </label>

                <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="override"
                    value="reassign"
                    checked={overrideAction === 'reassign'}
                    onChange={() => setOverrideAction('reassign')}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-2">Reassign Previous Booking</div>
                    <div className="text-sm text-gray-600 mb-2">
                      Assign the existing booking to a different driver
                    </div>
                    {overrideAction === 'reassign' && (
                      <select
                        value={newDriverForReassign}
                        onChange={(e) => setNewDriverForReassign(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                      >
                        <option value="">Select new driver...</option>
                        {drivers.filter(d => (d._id || d.id) !== currentDriverId).map((driver) => (
                          <option key={driver._id || driver.id} value={driver._id || driver.id}>
                            {driver.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConflictOverride}
                  disabled={overrideAction === 'reassign' && !newDriverForReassign}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Override
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverRequestsModal;

